import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Target, TrendingUp, Users, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageShell, PageHero } from "@/components/PageShell";
import legalImg from "@/assets/legal.jpg";
import expertGuidanceImg from "@/assets/expert-guidance.webp";

export const Route = createFileRoute("/strategic-consulting")({
  head: () => ({
    meta: [
      { title: "Strategic Consulting — Comm42 SA" },
      { name: "description", content: "Strategic guidance, legal determination and risk analysis for individuals, companies and organizations worldwide." },
      { property: "og:title", content: "Strategic Consulting — Comm42 SA" },
      { property: "og:description", content: "We build the right strategy to achieve your goals." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://comm42.ch/strategic-consulting" },
    ],
    links: [
      { rel: "canonical", href: "https://comm42.ch/strategic-consulting" },
    ],
  }),
  component: Page,
});

function Page() {
  const { t } = useTranslation();
  const services = [
    { Icon: Target, title: t("strategic.items.legal.title"), desc: t("strategic.items.legal.desc") },
    { Icon: TrendingUp, title: t("strategic.items.risk.title"), desc: t("strategic.items.risk.desc") },
    { Icon: Shield, title: t("strategic.items.web.title"), desc: t("strategic.items.web.desc") },
    { Icon: Users, title: t("strategic.items.logistics.title"), desc: t("strategic.items.logistics.desc") },
  ];
  return (
    <PageShell>
      <PageHero
        eyebrow={t("strategic.eyebrow")}
        title={t("strategic.title")}
        subtitle={t("strategic.subtitle")}
      />
      <section className="mx-auto max-w-7xl px-6 py-24">
        <h2 className="sr-only">{t("strategic.title")}</h2>
        <div className="grid gap-8 md:grid-cols-2">
          {services.map(({ Icon, title, desc }) => (
            <div key={title} className="border border-border/40 p-10 transition-colors hover:border-primary/60">
              <Icon className="h-8 w-8 text-primary" />
              <h3 className="mt-6 font-serif text-2xl">{title}</h3>
              <p className="mt-4 text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-card/40 py-24">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 md:grid-cols-2 md:items-center">
          <img src={expertGuidanceImg} alt="Strategic planning meeting" loading="lazy" width={1140} height={684} className="aspect-[1140/684] w-full border border-border/40 object-cover" />
          <div>
            <p className="text-xs tracking-[0.4em] text-primary">{t("strategic.expertEyebrow")}</p>
            <h2 className="mt-6 font-serif text-4xl">{t("strategic.expertTitle")}</h2>
            <p className="mt-6 text-muted-foreground">{t("strategic.expertDesc")}</p>
            <ul className="mt-8 space-y-4">
              {[t("strategic.bullets.a"), t("strategic.bullets.b"), t("strategic.bullets.c")].map((b) => (
                <li key={b} className="flex items-start gap-3 text-foreground/90">
                  <span className="mt-2 h-px w-6 bg-primary" /> {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-16 px-6 py-24 md:grid-cols-2 md:items-center">
        <div>
          <p className="text-xs tracking-[0.4em] text-primary">{t("strategic.legalEyebrow")}</p>
          <h2 className="mt-6 font-serif text-4xl">{t("strategic.legalTitle")}</h2>
          <p className="mt-6 text-muted-foreground">{t("strategic.legalDesc")}</p>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 border border-primary px-8 py-3 text-xs tracking-[0.3em] text-primary hover:bg-primary hover:text-primary-foreground">
            {t("strategic.discuss")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <img src={legalImg} alt="Legal determination" loading="lazy" width={1140} height={684} className="aspect-[1140/684] w-full border border-border/40 object-cover" />
      </section>
    </PageShell>
  );
}